import { useState } from 'react';
import { Download, Printer, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import MorseCodeVisualizer from '@/components/MorseCodeVisualizer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessCardPage() {
  const [cardInfo, setCardInfo] = useState({
    name: 'Lindy Lace',
    title: 'Owner',
    email: 'lindylace.holt@gmail.com',
    phone: '97743134',
    website: 'www.whisperingwristwear.com',
    instagram: '@whisperingwristwear'
  });
  const [showBack, setShowBack] = useState(false);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="pt-28 pb-16 min-h-screen" data-testid="business-card-page">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-area, .print-area * {
            visibility: visible;
          }
          .print-area {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      <div className="container-custom px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-12 no-print">
          <p className="text-caption text-[#84A98C] mb-4">Brand Assets</p>
          <h1 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-4">
            Business Card
          </h1>
          <p className="text-[#52796F] max-w-lg mx-auto">
            Customize and print your Whispering Wristwear business cards
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Card Preview */}
          <div className="flex justify-center mb-8">
            <div className="print-area">
              {/* Card Container - Standard business card: 3.5" x 2" at 300dpi = 1050x600px, scaled down */}
              <div 
                className="relative transition-transform duration-500 cursor-pointer"
                style={{ 
                  width: '420px', 
                  height: '240px',
                  perspective: '1000px'
                }}
                onClick={() => setShowBack(!showBack)}
              >
                {/* Front of Card */}
                <div 
                  className={`absolute inset-0 rounded-lg shadow-float transition-all duration-500 overflow-hidden ${showBack ? 'opacity-0 rotate-y-180' : 'opacity-100'}`}
                  style={{
                    background: '#FAF9F6',
                    backfaceVisibility: 'hidden'
                  }}
                  data-testid="business-card-front"
                >
                  <img
                    src="/images/whispering-wristwear-logo.png"
                    alt="Whispering Wristwear — Handcrafted Morse Code Bracelets"
                    className="w-full h-full object-contain p-4"
                    data-testid="business-card-front-logo"
                  />
                </div>

                {/* Back of Card */}
                <div 
                  className={`absolute inset-0 rounded-lg shadow-float transition-all duration-500 ${showBack ? 'opacity-100' : 'opacity-0 rotate-y-180'}`}
                  style={{
                    background: '#FAF9F6',
                    backfaceVisibility: 'hidden'
                  }}
                >
                  <div className="h-full p-8 flex flex-col justify-between">
                    {/* Top - Name & Title */}
                    <div>
                      <h3 className="font-serif text-xl text-[#354F52]">{cardInfo.name}</h3>
                      <p className="text-sm text-[#84A98C] uppercase tracking-widest">{cardInfo.title}</p>
                    </div>

                    {/* Bottom - Contact Info */}
                    <div className="space-y-1 text-sm">
                      <p className="text-[#52796F]">{cardInfo.email}</p>
                      <p className="text-[#52796F]">{cardInfo.phone}</p>
                      <div className="flex items-center gap-4 pt-2">
                        <p className="text-[#354F52] font-medium">{cardInfo.website}</p>
                        <p className="text-[#84A98C]">{cardInfo.instagram}</p>
                      </div>
                    </div>

                    {/* Accent line */}
                    <div className="absolute bottom-0 left-0 right-0 h-2 bg-[#D4AF37]"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Flip instruction */}
          <p className="text-center text-sm text-[#84A98C] mb-8 no-print">
            Click card to flip • Showing {showBack ? 'back' : 'front'}
          </p>

          {/* Actions */}
          <div className="flex flex-wrap justify-center gap-3 mb-12 no-print">
            <Button
              onClick={() => setShowBack(!showBack)}
              variant="outline"
              className="border-[#CAD2C5] text-[#354F52] flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
              Flip Card
            </Button>
            <a
              href={`${API}/brand/business-card?side=front`}
              download
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#52796F] hover:bg-[#354F52] text-[#FAF9F6] text-sm transition-colors"
              data-testid="download-card-front"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              Download front (300 DPI PNG)
            </a>
            <a
              href={`${API}/brand/business-card?side=back&name=${encodeURIComponent(cardInfo.name)}&title=${encodeURIComponent(cardInfo.title)}&email=${encodeURIComponent(cardInfo.email)}&phone=${encodeURIComponent(cardInfo.phone)}&website=${encodeURIComponent(cardInfo.website)}&instagram=${encodeURIComponent(cardInfo.instagram)}`}
              download
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#52796F] hover:bg-[#354F52] text-[#FAF9F6] text-sm transition-colors"
              data-testid="download-card-back"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              Download back (300 DPI PNG)
            </a>
            <a
              href={`${API}/brand/business-card?side=both&name=${encodeURIComponent(cardInfo.name)}&title=${encodeURIComponent(cardInfo.title)}&email=${encodeURIComponent(cardInfo.email)}&phone=${encodeURIComponent(cardInfo.phone)}&website=${encodeURIComponent(cardInfo.website)}&instagram=${encodeURIComponent(cardInfo.instagram)}`}
              download
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#354F52] hover:bg-[#52796F] text-[#FAF9F6] text-sm transition-colors"
              data-testid="download-card-both"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              Print sheet (front + back)
            </a>
            <Button
              onClick={handlePrint}
              variant="outline"
              className="border-[#CAD2C5] text-[#354F52] flex items-center gap-2"
              data-testid="print-card-btn"
            >
              <Printer className="w-4 h-4" strokeWidth={1.5} />
              Browser print
            </Button>
          </div>

          {/* Logo downloads */}
          <div className="bg-[#F5F5F0] p-6 rounded-sm mb-12 no-print" data-testid="logo-downloads">
            <h2 className="font-serif text-xl text-[#354F52] mb-1">Brand logo · high resolution</h2>
            <p className="text-sm text-[#84A98C] mb-5">
              Square PNGs at 2000×2000 px (300 DPI). Use the dark logo on cream / paper / cards, the light logo on packaging or photo overlays.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href={`${API}/brand/logo?style=dark&size=2000`}
                download
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm bg-[#FAF9F6] border border-[#CAD2C5] text-[#354F52] text-sm hover:border-[#D4AF37] transition-colors"
                data-testid="download-logo-dark"
              >
                <Download className="w-4 h-4" strokeWidth={1.5} />
                Download logo · dark on cream
              </a>
              <a
                href={`${API}/brand/logo?style=light&size=2000`}
                download
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm bg-[#354F52] text-[#FAF9F6] text-sm hover:bg-[#52796F] transition-colors"
                data-testid="download-logo-light"
              >
                <Download className="w-4 h-4" strokeWidth={1.5} />
                Download logo · cream on sage
              </a>
            </div>
          </div>

          {/* Customization Form */}
          <div className="bg-[#F5F5F0] p-8 rounded-sm no-print">
            <h2 className="font-serif text-xl text-[#354F52] mb-6">Customize Your Card</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#354F52]">Name</Label>
                <Input
                  id="name"
                  value={cardInfo.name}
                  onChange={(e) => setCardInfo({ ...cardInfo, name: e.target.value })}
                  className="border-[#CAD2C5] focus:border-[#354F52]"
                  data-testid="card-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="title" className="text-[#354F52]">Title</Label>
                <Input
                  id="title"
                  value={cardInfo.title}
                  onChange={(e) => setCardInfo({ ...cardInfo, title: e.target.value })}
                  className="border-[#CAD2C5] focus:border-[#354F52]"
                  data-testid="card-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#354F52]">Email</Label>
                <Input
                  id="email"
                  value={cardInfo.email}
                  onChange={(e) => setCardInfo({ ...cardInfo, email: e.target.value })}
                  className="border-[#CAD2C5] focus:border-[#354F52]"
                  data-testid="card-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[#354F52]">Phone</Label>
                <Input
                  id="phone"
                  value={cardInfo.phone}
                  onChange={(e) => setCardInfo({ ...cardInfo, phone: e.target.value })}
                  className="border-[#CAD2C5] focus:border-[#354F52]"
                  data-testid="card-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="website" className="text-[#354F52]">Website</Label>
                <Input
                  id="website"
                  value={cardInfo.website}
                  onChange={(e) => setCardInfo({ ...cardInfo, website: e.target.value })}
                  className="border-[#CAD2C5] focus:border-[#354F52]"
                  data-testid="card-website-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="instagram" className="text-[#354F52]">Instagram</Label>
                <Input
                  id="instagram"
                  value={cardInfo.instagram}
                  onChange={(e) => setCardInfo({ ...cardInfo, instagram: e.target.value })}
                  className="border-[#CAD2C5] focus:border-[#354F52]"
                  data-testid="card-instagram-input"
                />
              </div>
            </div>
          </div>

          {/* Print Instructions */}
          <div className="mt-8 p-6 border border-[#CAD2C5]/50 rounded-sm no-print">
            <h3 className="font-serif text-lg text-[#354F52] mb-3">Printing Tips</h3>
            <ul className="space-y-2 text-sm text-[#52796F]">
              <li>• Standard business card size: 3.5" × 2" (89mm × 51mm)</li>
              <li>• For best results, use thick cardstock (300gsm or higher)</li>
              <li>• Consider matte finish for an elegant, artisanal feel</li>
              <li>• Gold foil stamping on the "&" and accent line adds luxury</li>
              <li>• For professional printing, download and send to a print service like Moo or Vistaprint</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
